import {
  AcordoDetails,
  DividaAcordo,
  ParcelaAcordo,
} from "core/features/acordo/typings";
import { defaultContentMapperXlsx } from "infra/packages/json-as-xlsx/index";
import xlsx, { IColumn, IContent, IJsonSheet, ISettings } from "json-as-xlsx";
import moment from "moment";
import { getSituacaoParcelaText } from "./getSituacaoParcelaText";
import { getStatusAcordoText } from "./getStatusAcordoText";

export function exportExcelAcordo({ data }: { data: AcordoDetails }) {
  const settings: ISettings = {
    fileName: `Detalhamento do Acordo - ${data.codigo || ""}`,
    extraLength: 3,
    writeOptions: {},
  };

  const columnsInformacoes = getColumnsInformacoes(data);
  const columnsEntrada = getColumnsEntrada();
  const columnsParcelas = getColumnsParcelas();
  const columnsReceitasAcordadas = getColumnsReceitasAcordadas();

  const sheets: IJsonSheet[] = [];

  sheets.push({
    sheet: `Informações`,
    columns: columnsInformacoes,
    content: [data].map(defaultContentMapperXlsx),
  });

  if (data.entrada) {
    sheets.push({
      sheet: "Entrada",
      columns: columnsEntrada,
      content: [data].map(defaultContentMapperXlsx),
    });
  }

  if (data.parcelas?.length) {
    sheets.push({
      sheet: "Parcelas",
      columns: columnsParcelas,
      content: data.parcelas
        .map((p, index) => ({ ...p, index }))
        .map(defaultContentMapperXlsx),
    });
  }

  if (data.dividasAcordo?.length) {
    sheets.push({
      sheet: "Receitas acordadas",
      columns: columnsReceitasAcordadas,
      content: data.dividasAcordo.map(defaultContentMapperXlsx),
    });
  }

  xlsx(sheets, settings);
}

function getColumnsInformacoes(data: AcordoDetails) {
  const columns: IColumn[] = [];

  columns.push({
    label: "Acordo",
    value: (row: IContent) => (row as unknown as AcordoDetails).codigo || "",
  });

  columns.push({
    label: "Status",
    value: (row: IContent) =>
      getStatusAcordoText(
        (row as unknown as AcordoDetails).status,
        (row as unknown as AcordoDetails).dataCancelamento
      ),
  });

  columns.push({
    label: "Unidade",
    value: (row: IContent) =>
      `${(row as unknown as AcordoDetails).unidade.numero ?? "--"} ${
        (row as unknown as AcordoDetails).unidade.blocoQuadra
          ? `, ${(row as unknown as AcordoDetails).unidade.blocoQuadra}`
          : ""
      }`,
  });

  columns.push({
    label: "Proprietário",
    value: (row: IContent) => (row as unknown as AcordoDetails).morador.nome,
  });

  if (data.diasEmAtraso) {
    columns.push({
      label: "Dias em atraso",
      value: (row: IContent) => (row as unknown as AcordoDetails).diasEmAtraso,
    });
  }

  return columns;
}

function getColumnsEntrada() {
  const columns: IColumn[] = [];

  columns.push({
    label: "Entrada",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as AcordoDetails).entrada?.valor || "",
  });

  columns.push({
    label: "Meio de Pagamento",
    value: (row: IContent) =>
      (row as unknown as AcordoDetails).entrada?.formaPagamento.nome || "",
  });

  return columns;
}

function getColumnsParcelas() {
  const columns: IColumn[] = [];

  columns.push({
    label: "Parcela",
    format: "#,###0.00",
    value: (row: IContent) =>
      `${(row as unknown as ParcelaAcordo & { index: number }).index + 1}ª`,
  });

  columns.push({
    label: "Vencimento",
    format: "dd/mm/yyyy",
    value: (row: IContent) =>
      moment((row as unknown as ParcelaAcordo).dataVencimento).format(
        "DD/MM/YYYY"
      ),
  });

  columns.push({
    label: "Valor da Parcela",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as ParcelaAcordo).valor,
  });

  columns.push({
    label: "Situação",
    value: (row: IContent) =>
      getSituacaoParcelaText(row as unknown as ParcelaAcordo),
  });

  return columns;
}

function getColumnsReceitasAcordadas() {
  const columns: IColumn[] = [];

  columns.push({
    label: "Vencimento",
    format: "dd/mm/yyyy",
    value: (row: IContent) =>
      moment((row as unknown as DividaAcordo).dataVencimento).format(
        "DD/MM/YYYY"
      ),
  });

  columns.push({
    label: "Valor original",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as DividaAcordo).valor,
  });

  columns.push({
    label: "Multa",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as DividaAcordo).valorMulta,
  });

  columns.push({
    label: "Juros",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as DividaAcordo).valorJuros,
  });

  columns.push({
    label: "Honorários",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as DividaAcordo).valorHonorarios,
  });

  columns.push({
    label: "Valor Atual",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as DividaAcordo).valorTotal,
  });

  return columns;
}
